import { useRecoilState, useSetRecoilState } from 'recoil';
import { useCallback, useMemo } from 'react';
import { isEmpty, union } from 'lodash';
import {
  CalendarEvent,
  RoutineActivity,
  ScheduledAction,
  SchedulerPreferenceV3,
  StreamChatSessionType,
  TaskItemForSchedule,
} from '@demind-inc/core';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';

import {
  acceptedEventScheduleIdsAtom,
  aiFeedbackModalVisibleAtom,
  aiScheduledActionsAtom,
  aiSchedulerErrorAtom,
  aiSchedulerFeedbackApiInfoAtom,
  aiSchedulerInputAtom,
  AISchedulerLogicElement,
  aiSchedulerLogicElementsAtom,
  generalSnackBarAtom,
  isAISchedulerSetupOverlayVisibleAtom,
  isRunningAISchedulerAtom,
  scheduleLogicAllElements,
  selectedCoreActionModalAtom,
  useAuthContext,
  useCalendarContext,
  usePreferences,
  useEventActions,
  useRunAIScheduler,
  useAssistantChat,
  aiSchedulerStatusTextAtom,
  aiSchedulerProcessingDateAtom,
  aiSchedulerWeekCompleteAtom,
  aiSchedulerV5StreamAbortControllerAtom,
  useSSEStreaming,
  calendarViewSwitcherAtom,
  selectedDateAtom,
  homeViewSwitcherAtom,
  suggestAllAIPlanStateAtom,
  collectPendingItemsForChat,
  ChatSuggestedEvent,
  transformResultJsonSchedulesToSuggestedEvents,
  clearWeeklyPlanCache,
} from '../data-access';
import type { SessionResponse } from '../data-access';
import {
  getAiScheduledActionStableId,
  getPlanResultSource,
  translateAIScheduledActionToTemporalCalendarEvent,
  transformUnifiedChatResponseToScheduleAction,
} from '../helpers';
import {
  discardPendingAllPlanSuggestionsByType,
  persistDiscardedPendingAllPlanSuggestionsByType,
  shouldDiscardCurrentAllPlanSuggestionsForDate,
} from '../helpers/allPlanSuggestions.helpers';
import { getCssVariable, trackEventMixpanel, useFeatureFlag } from '../utils';

interface UseAISchedulerParams {
  isDemo?: boolean;
}

const primaryColor = getCssVariable('--primary-color');

export const useAIScheduler = ({ isDemo = false }: UseAISchedulerParams = {}) => {
  const [isAISchedulerSetupOverlayVisible, setIsAISchedulerSetupOverlayVisible] = useRecoilState(
    isAISchedulerSetupOverlayVisibleAtom
  );
  const [_, setSelectedModal] = useRecoilState(selectedCoreActionModalAtom);
  const [isRunningAIScheduler, setIsRunningAIScheduler] = useRecoilState(isRunningAISchedulerAtom);
  const [aiScheduledActions, setAiScheduledActions] = useRecoilState(aiScheduledActionsAtom);
  const [aiSchedulerError, setAiSchedulerError] = useRecoilState(aiSchedulerErrorAtom);
  const [__, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);
  const [aiFeedbackDialogVisible, setAiFeedbackDialogVisible] = useRecoilState(
    aiFeedbackModalVisibleAtom
  );
  const [___, setAiSchedulerFeedbackApiInfo] = useRecoilState(aiSchedulerFeedbackApiInfoAtom);
  const [aiSchedulerLogicElements, setAiSchedulerLogicElements] = useRecoilState(
    aiSchedulerLogicElementsAtom
  );
  const [aiSchedulerInput, setAiSchedulerInput] = useRecoilState(aiSchedulerInputAtom);
  const [aiSchedulerStatusText, setAiSchedulerStatusText] =
    useRecoilState(aiSchedulerStatusTextAtom);
  const [, setAiSchedulerProcessingDate] = useRecoilState(aiSchedulerProcessingDateAtom);
  const [isWeekPlanningComplete, setIsWeekPlanningComplete] = useRecoilState(
    aiSchedulerWeekCompleteAtom
  );
  const [abortController, setAbortController] = useRecoilState(
    aiSchedulerV5StreamAbortControllerAtom
  );
  const setCalendarView = useSetRecoilState(calendarViewSwitcherAtom);
  const setSelectedDate = useSetRecoilState(selectedDateAtom);
  const setHomeViewSwitcher = useSetRecoilState(homeViewSwitcherAtom);
  const setSuggestAllAIPlanState = useSetRecoilState(suggestAllAIPlanStateAtom);

  const { user } = useAuthContext();
  const { schedulerPreferences } = usePreferences({ preferenceId: user.preferenceId! });
  const { clearMessages } = useAssistantChat();
  const { runAISchedulerV5Stream, runAIWeekPlanStream } = useRunAIScheduler();
  const { mainCalendar, calendarPreferences } = useCalendarContext();
  const { createCalendarEvent } = useEventActions();
  const { startStreaming, stopStreaming } = useSSEStreaming();
  const [acceptedEventScheduleIds, setAcceptedEventScheduleIds] = useRecoilState(
    acceptedEventScheduleIdsAtom
  );
  const hasSetDefaultPrefs = useMemo(() => !isEmpty(schedulerPreferences), [schedulerPreferences]);
  const canResetAISchedule = useMemo(() => !!aiScheduledActions.length, [aiScheduledActions]);

  const planResultSourceById = useMemo(() => {
    const sources: Record<string, 'todo' | 'routine'> = {};
    aiScheduledActions.forEach((action) => {
      const source = getPlanResultSource(action);
      if (!source) {
        return;
      }
      sources[getAiScheduledActionStableId(action)] = source;
    });
    return sources;
  }, [aiScheduledActions]);

  const aiScheduledActionsEvents = useMemo(() => {
    const newEventCalendarId = mainCalendar?.calendarId!;
    const color = mainCalendar?.color || primaryColor;

    return aiScheduledActions.map((action) => {
      const taskLabels = action.todoFrom?.taskId
        ? aiSchedulerInput?.todos?.find((task) => task.taskId === action.todoFrom?.taskId)?.labels
        : undefined;

      return {
        ...translateAIScheduledActionToTemporalCalendarEvent(action, { taskLabels }),
        calendarId: newEventCalendarId,
        color,
      } as CalendarEvent;
    });
  }, [aiScheduledActions, aiSchedulerInput?.todos, mainCalendar]);

  const isSaveModalVisible = useMemo(
    () => !!aiScheduledActionsEvents.length,
    [aiScheduledActionsEvents]
  );

  const handleAISchedulreSetupOverlayVisible = (visible: boolean) => {
    setIsAISchedulerSetupOverlayVisible(visible);
  };

  const handleRunAIScheduler = async (
    preferences: SchedulerPreferenceV3,
    date: string,
    todos?: TaskItemForSchedule[],
    routine?: RoutineActivity[],
    userMessage?: string,
    scope: 'day' | 'week' = 'day',
    rangeEndDate?: string
  ) => {
    if (!user.userId) {
      return;
    }
    setIsRunningAIScheduler(true);
    setIsWeekPlanningComplete(scope !== 'week');

    const hasTodoEnabled = aiSchedulerLogicElements.includes('task');
    const targetDate = isDemo
      ? dayjs(date).startOf('day') // If demo, use start of day
      : dayjs(date).isSame(dayjs(), 'day')
        ? dayjs()
        : dayjs(date); // if date is today, use current time
    const weekStartDate = dayjs(targetDate).startOf('day');
    const weekEndDate = dayjs(
      rangeEndDate || dayjs(targetDate).startOf('week').add(5, 'day')
    ).startOf('day');
    const weekNumDays = Math.max(1, weekEndDate.diff(weekStartDate, 'day') + 1);

    if (scope === 'week') {
      setHomeViewSwitcher('Calendar');
      setCalendarView('week');
      setSelectedDate(dayjs(targetDate).startOf('day'));
    }

    // Store parameters for later use in handleAdjustAIScheduler
    setAiSchedulerInput({
      preferences,
      date: targetDate.toISOString(),
      todos,
      routine,
      userMessage,
    });

    let predictedActions: typeof aiScheduledActions = [];
    let scienceApiReq: string | undefined;
    let scienceApiRes: string | undefined;
    let schedulerSummary: string | null = null;
    let hasWeekCompleteEvent = false;

    try {
      // Abort any existing stream
      if (abortController) {
        abortController.abort();
      }
      // Create new abort controller
      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      // Reset status text array for V5
      setAiSchedulerStatusText([]);
      if (scope === 'week') {
        const targetDates = Array.from({ length: weekNumDays }, (_, index) =>
          weekStartDate.add(index, 'day').format('YYYY-MM-DD')
        );
        setAiSchedulerProcessingDate(targetDates);
      } else {
        setAiSchedulerProcessingDate([]);
      }
      const commonStreamHandlers = {
        abortController: newAbortController,
        onStatusText: (statusText: string) => {
          if (newAbortController.signal.aborted) {
            return;
          }
          setAiSchedulerStatusText((prev) => [...prev, statusText]);
        },
        onResult: (result: { schedules: ScheduledAction[]; summary?: string | null }) => {
          if (newAbortController.signal.aborted) {
            return;
          }
          if (scope !== 'week' || hasWeekCompleteEvent) {
            setAiSchedulerStatusText([]);
          }
          predictedActions = result.schedules;
          schedulerSummary = result.summary ?? null;
          setAiScheduledActions(result.schedules);

          const scheduleError = (result as { error?: string | null }).error ?? null;
          if (scheduleError === null && result.schedules.length === 0) {
            const summaryMessage = result.summary?.trim();
            setGeneralSnackbar(summaryMessage || 'No events to schedule.');
          }
        },
        onError: (error: Error) => {
          // Don't show error if it was aborted
          if (error.message === 'Stream aborted' || error.name === 'AbortError') {
            return;
          }
          setAiSchedulerStatusText([]);
          setAiSchedulerError(error.message);
          setGeneralSnackbar(error.message);
        },
        onStreamEnd: () => {
          if (scope !== 'week' || hasWeekCompleteEvent) {
            setAiSchedulerStatusText([]);
          }
          setIsRunningAIScheduler(false);
          setSelectedModal(null);
          setAbortController(null);
          setAiSchedulerProcessingDate([]);
        },
      };

      const selectedTasks = todos?.filter((t) => t.selected);
      const selectedRoutine = routine?.filter((r) => r.selected);

      // Use dedicated weekly planner stream for week scope.
      const response =
        scope === 'week'
          ? await runAIWeekPlanStream({
              userId: user.userId,
              metricId: user.metricId!,
              weekStart: dayjs(targetDate).format('YYYY-MM-DD'),
              numDays: weekNumDays,
              preferenceId: user.preferenceId!,
              routineId: user.routineId!,
              tasks: selectedTasks,
              routine: selectedRoutine,
              userMessage: userMessage || '',
              currentDate: dayjs().format('YYYY-MM-DD'),
              currentTime: dayjs().format('HH:mm'),
              onDayStart: () => {
                // Keep all target dates highlighted while planning a week.
                // We intentionally don't switch to a single active date here.
              },
              onDayComplete: ({ date: completedDate }) => {
                // Remove completed day indicator from week header.
                setAiSchedulerProcessingDate((prevDates) =>
                  prevDates.filter((targetDate) => targetDate !== completedDate)
                );
              },
              onWeekComplete: ({ summary }) => {
                if (summary) {
                  schedulerSummary = summary;
                }
                hasWeekCompleteEvent = true;
                setIsWeekPlanningComplete(true);
                setAiSchedulerStatusText([]);
                setAiSchedulerProcessingDate([]);
              },
              ...commonStreamHandlers,
            })
          : await runAISchedulerV5Stream({
              userId: user.userId,
              metricId: user.metricId!,
              targetDate: dayjs(targetDate).format('YYYY-MM-DD'),
              preferenceId: user.preferenceId!,
              routineId: user.routineId!,
              tasks: selectedTasks,
              routine: selectedRoutine,
              userMessage: userMessage || '',
              ...commonStreamHandlers,
            });

      predictedActions = response.schedules;
      scienceApiReq = response.scienceApiReq;
      scienceApiRes = response.scienceApiRes;
      schedulerSummary = response.summary ?? null;

      setAiSchedulerFeedbackApiInfo({
        appApiRes: JSON.stringify(predictedActions),
        scienceApiReq,
        scienceApiRes,
        currentTime: targetDate.toISOString(),
        preference: JSON.stringify(preferences),
      });
      setAiScheduledActions(predictedActions);
      if (
        scope === 'day' &&
        shouldDiscardCurrentAllPlanSuggestionsForDate(targetDate.toISOString())
      ) {
        setSuggestAllAIPlanState((prev) =>
          discardPendingAllPlanSuggestionsByType(prev, ['todo', 'routine'])
        );
        persistDiscardedPendingAllPlanSuggestionsByType(
          ['todo', 'routine'],
          targetDate.format('YYYY-MM-DD')
        );
      }
    } catch (error) {
      // A user-initiated stop aborts the stream; that is not a failure.
      if (
        error instanceof Error &&
        (error.message === 'Stream aborted' || error.name === 'AbortError')
      ) {
        setAiSchedulerProcessingDate([]);
        setIsWeekPlanningComplete(true);
        return;
      }
      setAiSchedulerError("Couldn't schedule those events.");
      setGeneralSnackbar("Scheduling didn't work");
      setAiSchedulerProcessingDate([]);
      setIsWeekPlanningComplete(true);
    } finally {
      setIsRunningAIScheduler(false);
      setSelectedModal(null);
      if (isDemo) {
        trackEventMixpanel('run_ai_scheduler_practice');
      } else {
        trackEventMixpanel('run_ai_scheduler');
      }
    }
    return schedulerSummary;
  };

  const handleAdjustAIScheduler = async (
    chatUserInput: string,
    chatSessionId?: string,
    onMessage?: (delta: string) => void,
    onQuestion?: (question: any) => void,
    hiddenContext?: Record<string, unknown>,
    onWholeMessage?: (message: string, textId: number) => void,
    onSuggestedBlocks?: (blocks: ChatSuggestedEvent[]) => void
  ) => {
    if (!user.userId || !aiSchedulerInput) {
      return;
    }

    // Abort any existing operation (run or adjust)
    if (abortController) {
      abortController.abort();
    }
    setAbortController(null);
    // Stop any ongoing SSE stream
    stopStreaming();
    setAiSchedulerStatusText([]);

    const targetDate = aiSchedulerInput.date;
    trackEventMixpanel('run_ai_scheduler_reschedule');

    return await new Promise<SessionResponse | undefined>((resolve, reject) => {
      const handleStatusText = (statusText: string | null) => {
        if (abortController?.signal.aborted) {
          return;
        }
        if (statusText === null) {
          setAiSchedulerStatusText([]);
          return;
        }
        setAiSchedulerStatusText((prev) => {
          if (prev[prev.length - 1] === statusText) {
            return prev;
          }
          return [...prev, statusText];
        });
      };

      startStreaming({
        sessionType: 'scheduler_chat',
        sessionId: chatSessionId ?? undefined,
        prompt: chatUserInput,
        targetDate: dayjs(targetDate).format('YYYY-MM-DD'),
        pendingItems: collectPendingItemsForChat({
          aiScheduledActions,
          allPlanAcceptedIds: acceptedEventScheduleIds ?? [],
        }),
        onMessage: onMessage || (() => void 0),
        onWholeMessage,
        onStatusText: handleStatusText,
        onQuestion,
        hiddenContext,
        onResultOut: (result) => {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            const resultJson = parsedResult?.result_json ?? parsedResult;

            // Check if it's unified_chat_responses format
            if (resultJson?.unified_chat_responses) {
              const suggestedBlocks = resultJson.unified_chat_responses.flatMap((entry: any) =>
                transformResultJsonSchedulesToSuggestedEvents(
                  entry?.adjustments?.schedule ?? [],
                  entry?.date || dayjs(targetDate).format('YYYY-MM-DD')
                )
              );
              onSuggestedBlocks?.(suggestedBlocks);

              const hasTodoEnabled = aiSchedulerLogicElements.includes('task');
              const transformedSchedules = transformUnifiedChatResponseToScheduleAction(
                resultJson,
                hasTodoEnabled ? aiSchedulerInput.todos?.filter((t) => t.selected) : undefined
              );

              setAiSchedulerFeedbackApiInfo({
                appApiRes: JSON.stringify(resultJson),
                scienceApiReq: '',
                scienceApiRes: '',
                currentTime: targetDate,
                preference: JSON.stringify(aiSchedulerInput.preferences),
              });

              if (!Array.isArray(transformedSchedules) || transformedSchedules.length === 0) {
                const scheduleError = resultJson?.error ?? parsedResult?.error ?? null;
                if (scheduleError === null) {
                  const summaryMessage =
                    (resultJson?.summary ?? parsedResult?.summary ?? null)?.trim?.() || null;
                  setGeneralSnackbar(summaryMessage || 'No events to schedule.');
                }
                setAiSchedulerError('No events to schedule.');
                return;
              }

              setAiSchedulerError(null);
              // Merge new schedules with existing ones
              setAiScheduledActions((prev) => {
                const existingIds = new Set(prev.map((action) => action.id).filter(Boolean));
                const newSchedules = transformedSchedules.filter(
                  (schedule) => !schedule.id || !existingIds.has(schedule.id)
                );
                const updatedSchedules = prev.map((existing) => {
                  const updated = transformedSchedules.find(
                    (schedule) => schedule.id && schedule.id === existing.id
                  );
                  return updated || existing;
                });
                return [...updatedSchedules, ...newSchedules];
              });
            } else {
              // Fallback to old format
              const schedules =
                resultJson?.schedule || resultJson?.schedules || parsedResult?.schedule || [];

              setAiSchedulerFeedbackApiInfo({
                appApiRes: JSON.stringify(resultJson),
                scienceApiReq: '',
                scienceApiRes: '',
                currentTime: targetDate,
                preference: JSON.stringify(aiSchedulerInput.preferences),
              });

              if (!Array.isArray(schedules) || schedules.length === 0) {
                const scheduleError = resultJson?.error ?? parsedResult?.error ?? null;
                if (scheduleError === null) {
                  const summaryMessage =
                    (resultJson?.summary ?? parsedResult?.summary ?? null)?.trim?.() || null;
                  setGeneralSnackbar(summaryMessage || 'No events to schedule.');
                }
                setAiSchedulerError('No events to schedule.');
                return;
              }

              setAiSchedulerError(null);
              // Merge new schedules with existing ones
              setAiScheduledActions((prev) => {
                const existingIds = new Set(prev.map((action) => action.id).filter(Boolean));
                const newSchedules = schedules.filter(
                  (schedule: ScheduledAction) =>
                    !schedule.id ||
                    !existingIds.has(schedule.id) ||
                    schedule.actionType !== 'remove'
                );
                const updatedSchedules = prev.map((existing) => {
                  const updated = schedules.find(
                    (schedule: ScheduledAction) =>
                      schedule.id && schedule.id === existing.id && schedule.actionType !== 'remove'
                  );
                  return updated || existing;
                });
                return [...updatedSchedules, ...newSchedules];
              });
            }
          } catch (error) {
            reject(error);
          }
        },
        onError: (error) => {
          const errorMessage =
            typeof error === 'string' && error.trim()
              ? error
              : 'Streaming error occurred while adjusting schedule.';
          setAiSchedulerError(errorMessage);
          setGeneralSnackbar(errorMessage);
          setAiSchedulerStatusText([]);
          reject(new Error(errorMessage));
        },
        onStreamEnd: () => {
          setAiSchedulerStatusText([]);
          resolve(undefined);
        },
      });
    });
  };

  /** Stop an in-flight run (day or week) without clearing the thread. */
  const stopAiScheduler = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    stopStreaming();
    setIsRunningAIScheduler(false);
    setAiSchedulerStatusText([]);
    setAiSchedulerProcessingDate([]);
    setIsWeekPlanningComplete(true);
  }, [
    abortController,
    setAbortController,
    setIsRunningAIScheduler,
    setAiSchedulerStatusText,
    setAiSchedulerProcessingDate,
    setIsWeekPlanningComplete,
    stopStreaming,
  ]);

  const resetAiScheduler = useCallback(() => {
    // Abort any ongoing V5 stream
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    stopStreaming();
    setIsRunningAIScheduler(false);
    setAiFeedbackDialogVisible(false);
    setAiScheduledActions([]);
    setSelectedModal(null);
    setAiSchedulerError(null);
    setAiSchedulerFeedbackApiInfo(null);
    setAiSchedulerInput(null);
    setAcceptedEventScheduleIds(null);
    setAiSchedulerStatusText([]);
    setAiSchedulerProcessingDate([]);
    setIsWeekPlanningComplete(true);
    // Otherwise a refresh right after discarding/accepting would resurrect
    // the week plan this reset just cleared.
    clearWeeklyPlanCache();
    clearMessages();
  }, [
    abortController,
    setAbortController,
    setIsRunningAIScheduler,
    setAiFeedbackDialogVisible,
    clearMessages,
    setSelectedModal,
    setAiSchedulerError,
    setAiSchedulerFeedbackApiInfo,
    setAiSchedulerInput,
    setAcceptedEventScheduleIds,
    setAiSchedulerStatusText,
    setAiSchedulerProcessingDate,
    setIsWeekPlanningComplete,
    stopStreaming,
  ]);

  const handleAcceptAIScheduleEvent = useCallback(
    async (event: Partial<CalendarEvent>, calendarId?: string, skipEfficiencyCalc = false) => {
      const taskFrom = event.taskFrom;
      const isTaskLinked = !!(taskFrom?.taskId && taskFrom?.boardId);
      const transparency = calendarPreferences?.transparency as
        | { aiScheduler?: CalendarEvent['showAs']; task?: CalendarEvent['showAs'] }
        | undefined;

      await createCalendarEvent({
        calendarId: calendarId || mainCalendar?.calendarId!,
        ...(isTaskLinked ? { taskId: taskFrom.taskId } : {}),
        newEventOption: {
          ...event,
          eventId: (uuidv4() as string).replace(/-/g, ''),
          color: mainCalendar?.color || primaryColor,
          scheduleFrom: isTaskLinked ? 'todo' : event.scheduleFrom || 'aiScheduler',
          ...(isTaskLinked
            ? {
                autoScheduleFrom: {
                  from: 'todo',
                  id: taskFrom.taskId,
                  predicted: false,
                },
              }
            : event.autoScheduleFrom
              ? { autoScheduleFrom: { ...event.autoScheduleFrom, predicted: false } }
              : {}),
          showAs: isTaskLinked
            ? transparency?.task || transparency?.aiScheduler || 'available'
            : transparency?.aiScheduler || 'available',
          unSyncToCalendar: isTaskLinked
            ? !!calendarPreferences?.unSyncToCalendar?.task
            : !!calendarPreferences?.unSyncToCalendar?.aiScheduler,
        },
      });
      const acceptedEventScheduleId = event.autoScheduleFrom?.id;

      if (acceptedEventScheduleId) {
        setAcceptedEventScheduleIds((prev) => union(prev, [acceptedEventScheduleId]));
        setAiScheduledActions((prev) =>
          prev.filter((a) => getAiScheduledActionStableId(a) !== acceptedEventScheduleId)
        );
      }
    },
    [
      user.userId,
      mainCalendar,
      calendarPreferences?.transparency,
      calendarPreferences?.unSyncToCalendar,
      createCalendarEvent,
      setAiScheduledActions,
    ]
  );

  const handleAcceptAllEvents = useCallback(async () => {
    const unacceptedEvents = aiScheduledActionsEvents.filter(
      (event) => !acceptedEventScheduleIds?.includes(event.autoScheduleFrom?.id)
    );

    await Promise.all(
      unacceptedEvents.map((event) => handleAcceptAIScheduleEvent(event, undefined, true))
    );

    trackEventMixpanel('accept_all_ai_scheduled_events');
    resetAiScheduler();
  }, [
    aiScheduledActionsEvents,
    handleAcceptAIScheduleEvent,
    resetAiScheduler,
    acceptedEventScheduleIds,
  ]);

  const handleDiscard = useCallback(() => {
    setAiFeedbackDialogVisible(true);
  }, [setAiFeedbackDialogVisible]);

  const handleDiscardEvent = useCallback(
    (actionId: string) => {
      setAiScheduledActions((prev) =>
        prev.filter((a) => {
          const stableId = getAiScheduledActionStableId(a);
          return stableId !== actionId && a.id !== actionId;
        })
      );
    },
    [setAiScheduledActions]
  );

  const handleDiscardEvents = useCallback(
    (actionIds: string[]) => {
      const idSet = new Set(actionIds);
      setAiScheduledActions((prev) =>
        prev.filter((a) => {
          const stableId = getAiScheduledActionStableId(a);
          return !idSet.has(stableId) && (!a.id || !idSet.has(a.id));
        })
      );
    },
    [setAiScheduledActions]
  );

  const handleConfirmDiscard = useCallback(() => {
    resetAiScheduler();
    setAiFeedbackDialogVisible(false);
  }, [resetAiScheduler, setAiFeedbackDialogVisible]);

  const handleFeedbackDialogClose = useCallback(() => {
    setAiFeedbackDialogVisible(false);
  }, [setAiFeedbackDialogVisible]);

  const handleToggleAiSchedulerLogicElement = (
    element: AISchedulerLogicElement,
    enabled: boolean
  ) => {
    setAiSchedulerLogicElements((prevElements) => {
      if (enabled) {
        return [...prevElements, element];
      }
      return prevElements.filter((e) => e !== element);
    });
  };

  return {
    aiSchedulerError,
    isRunningAIScheduler,
    isAISchedulerSetupOverlayVisible,
    canResetAISchedule,
    aiScheduledActions,
    aiScheduledActionsEvents,
    planResultSourceById,
    hasSetDefaultPrefs,
    schedulerPreferences,
    aiFeedbackDialogVisible,
    aiSchedulerLogicElements,
    isSaveModalVisible,
    aiSchedulerStatusText,
    isWeekPlanningComplete,
    handleToggleAiSchedulerLogicElement,
    handleAdjustAIScheduler,
    handleDiscardEvent,
    handleDiscardEvents,
    handleFeedbackDialogClose,
    handleConfirmDiscard,
    handleDiscard,
    handleAcceptAIScheduleEvent,
    handleAcceptAllEvents,
    resetAiScheduler,
    stopAiScheduler,
    handleRunAIScheduler,
    handleAISchedulreSetupOverlayVisible,
  };
};
